import { createRef } from 'react';
import JxLineEdit from '../../../../JxControls/JxEdits/JxLineEdit';
import commandEngine from './Commands/CommandEngine';
import CmdEditOptions from './CmdEditOptions';
import './CmdBarEdit.css';

/**
 * CmdBarEdit — extends JxLineEdit with slash command autocomplete.
 *
 * Behaviour:
 *   - User types '/' → dropdown shows all registered commands
 *   - User types '/fo' → dropdown filters to matching commands
 *   - User selects a command → dropdown switches to param options for first param
 *   - User types param value → dropdown filters param options to matching labels
 *   - User presses Tab → selects first matching command or param option
 *   - User clicks a param option → param value inserted, advances to next param
 *   - Enter executes, Escape closes dropdown
 *   - Dropdown flips above the input when there isn't enough space below
 *
 * Param rows use { label, hint } internally:
 *   label — display text in the dropdown (e.g. 'Amsterdam')
 *   hint  — value inserted into the input (e.g. 'Europe/Amsterdam')
 *
 * @param {string}   props.id
 * @param {string}   props.value
 * @param {function} props.onChange
 * @param {function} props.onExecute  - (value: string) => void
 * @param {string}   [props.placeholder]
 */
export default class CmdBarEdit extends JxLineEdit {
    constructor(props) {
        super(props);
        this._wrapRef = createRef();
        this._dropdownRef = createRef();
        this.state = {
            mode: 'none',  // 'none' | 'commands' | 'params'
            matches: [],   // command matches { cmd, hint, params }
            params: [],    // filtered param rows: { name, label, hint }
            activeIndex: -1,
            dropUp: false, // true when dropdown is flipped above the input
        };
    }

    getPropsToOmit() {
        return ['onExecute'];
    }

    getPlaceholder() {
        return this.props.placeholder || 'Search or /command…';
    }

    // ── matching ──────────────────────────────────────────

    /**
     * Expands a param definition into { name, label, hint } rows.
     *
     * Supported param shapes:
     *   { name, hints: string[] }                  — label and hint both = the string value
     *   { name, options: { label, value }[] }       — label for display, value (hint) for insertion
     *   { name, hint: string }                      — single row, label = hint
     *
     * @param {{ name: string, hint?: string, hints?: string[], options?: { label: string, value: string }[] }} param
     * @returns {{ name: string, label: string, hint: string }[]}
     */
    _expandParam(param) {
        if (param.options) {
            return param.options.map((o) => ({ name: param.name, label: o.label, hint: o.value }));
        }
        if (param.hints) {
            return param.hints.map((h) => ({ name: param.name, label: h, hint: h }));
        }
        return [{ name: param.name, label: param.hint ?? param.name, hint: param.hint ?? '' }];
    }

    /**
     * Filters expanded param rows by the value the user is currently typing.
     * Matches against label (display text). Empty typed value returns all rows unfiltered.
     * @param {{ name: string, label: string, hint: string }[]} expanded
     * @param {string} typedValue
     * @returns {{ name: string, label: string, hint: string }[]}
     */
    _filterParams(expanded, typedValue) {
        if (!typedValue) return expanded;
        const lower = typedValue.toLowerCase();
        return expanded.filter((p) => p.label.toLowerCase().startsWith(lower));
    }

    _updateDropdown(value) {
        if (!value.startsWith('/')) {
            this.setState({ mode: 'none', matches: [], params: [], activeIndex: -1 });
            return;
        }

        const parts = value.split(' ');

        if (parts.length > 1) {
            // Command typed — determine which param index we're on
            // parts[0] = cmd, parts[1..] = param values typed so far
            // last part is the token currently being typed
            const cmd = parts[0];
            const def = commandEngine.getCommand(cmd);
            const allParams = def?.params ?? [];

            if (!allParams.length) {
                this.setState({ mode: 'none', params: [], matches: [], activeIndex: -1 });
                return;
            }

            // params before the one being typed
            const typedParamCount = parts.length - 2;
            const paramIndex = Math.min(typedParamCount, allParams.length - 1);
            const currentParam = allParams[paramIndex];
            const expanded = this._expandParam(currentParam);

            // filter by what the user has typed so far for this param
            const typedValue = parts[parts.length - 1];
            const filtered = this._filterParams(expanded, typedValue);

            this.setState({
                mode: filtered.length ? 'params' : 'none',
                params: filtered,
                matches: [],
                activeIndex: -1,
            });
            return;
        }

        const matches = commandEngine.getMatches(value);
        this.setState({ mode: matches.length ? 'commands' : 'none', matches, params: [], activeIndex: -1 });
    }

    _selectCommand(match) {
        const params = match.params ?? [];
        this.props.onChange(match.cmd + ' ');
        const firstParam = params[0] ?? null;
        const expanded = firstParam ? this._expandParam(firstParam) : [];
        this.setState({
            mode: expanded.length ? 'params' : 'none',
            params: expanded,
            matches: [],
            activeIndex: -1,
        });
    }

    _selectParam(param) {
        // Replace last (incomplete) token with the selected hint (value) + space
        const parts = (this.props.value ?? '').split(' ');
        parts[parts.length - 1] = param.hint;
        this.props.onChange(parts.join(' ') + ' ');

        // Advance to next param
        const cmd = parts[0];
        const def = commandEngine.getCommand(cmd);
        const allParams = def?.params ?? [];
        const nextIndex = parts.length - 1; // after appending hint, parts.length - 1 = next param index
        const nextParam = allParams[nextIndex] ?? null;
        const expanded = nextParam ? this._expandParam(nextParam) : [];

        this.setState({
            mode: expanded.length ? 'params' : 'none',
            params: expanded,
            activeIndex: -1,
        });
    }

    // ── dropdown placement ────────────────────────────────

    /**
     * Measures available space above/below the input and flips the dropdown
     * above the input (dropUp) when there isn't enough room below.
     */
    _updateDropUp() {
        if (!this._wrapRef.current || !this._dropdownRef.current) return;

        const wrapRect = this._wrapRef.current.getBoundingClientRect();
        const ddHeight = this._dropdownRef.current.offsetHeight;
        const spaceBelow = window.innerHeight - wrapRect.bottom;
        const spaceAbove = wrapRect.top;

        const shouldDropUp = spaceBelow < ddHeight + 8 && spaceAbove > spaceBelow;
        if (shouldDropUp !== this.state.dropUp) {
            this.setState({ dropUp: shouldDropUp });
        }
    }

    // ── JxLineEdit overrides ──────────────────────────────

    handleChange(e) {
        const next = e.target.value;
        this.props.onChange(next);
        this._updateDropdown(next);
    }

    handleKeyDown(e) {
        const { mode, matches, params, activeIndex } = this.state;
        const items = mode === 'commands' ? matches : mode === 'params' ? params : [];

        if (items.length > 0) {
            if (e.key === 'ArrowDown') {
                e.preventDefault();
                this.setState({ activeIndex: Math.min(activeIndex + 1, items.length - 1) });
                return;
            }
            if (e.key === 'ArrowUp') {
                e.preventDefault();
                this.setState({ activeIndex: Math.max(activeIndex - 1, 0) });
                return;
            }

            if (e.key === 'Enter' && activeIndex >= 0) {
                e.preventDefault();
                e.stopPropagation();
                if (mode === 'commands') this._selectCommand(matches[activeIndex]);
                else this._selectParam(params[activeIndex]);
                return;
            }

            if (e.key === 'Tab') {
                e.preventDefault();
                e.stopPropagation();
                if (mode === 'commands') this._selectCommand(matches[0]);
                else this._selectParam(params[0]);
                return;
            }

            if (e.key === 'Escape') {
                e.preventDefault();
                e.stopPropagation();
                this.setState({ mode: 'none', matches: [], params: [], activeIndex: -1 });
                return;
            }
        }

        if (e.key === 'Enter') {
            e.preventDefault();
            const value = this.props.value ?? '';
            if (value.trim()) this.props.onExecute?.(value.trim());
            this.setState({ mode: 'none', params: [], activeIndex: -1 });
            return;
        }

        super.handleKeyDown(e);
    }

    // ── click outside ─────────────────────────────────────

    componentDidMount() {
        document.addEventListener('mousedown', this._handleClickOutside);
    }

    componentDidUpdate(prevProps, prevState) {
        if (
            prevState.mode !== this.state.mode ||
            prevState.matches !== this.state.matches ||
            prevState.params !== this.state.params
        ) {
            this._updateDropUp();
        }
    }

    componentWillUnmount() {
        document.removeEventListener('mousedown', this._handleClickOutside);
    }

    _handleClickOutside = (e) => {
        if (this._wrapRef.current && !this._wrapRef.current.contains(e.target)) {
            this.setState({ mode: 'none', matches: [], params: [], activeIndex: -1 });
        }
    };

    // ── render ────────────────────────────────────────────

    render() {
        const { mode, matches, params, activeIndex, dropUp } = this.state;
        const showDropdown = mode === 'commands' || mode === 'params';

        return (
            <div ref={this._wrapRef} className="cbe-wrap">
                {super.render()}

                {showDropdown && (
                    <CmdEditOptions
                        mode={mode}
                        matches={matches}
                        params={params}
                        activeIndex={activeIndex}
                        dropUp={dropUp}
                        innerRef={this._dropdownRef}
                        onSelectCommand={(m) => this._selectCommand(m)}
                        onSelectParam={(p) => this._selectParam(p)}
                    />
                )}
            </div>
        );
    }
}
